import {
  TMDB_API_KEY,
  TMDB_BASE_URL,
  TMDB_IMAGE_BASE_URL,
  TMDB_PROFILE_BASE_URL,
  TMDB_LARGE_PROFILE_BASE_URL,
  TMDB_BACKDROP_BASE_URL,
} from "../constants/apiConstants.js";

/**
 * Map TMDB movie object to our app's movie format
 * @param {Object} tmdbMovie - Raw TMDB movie object
 * @param {Object} genresMap - Map of genre IDs to names
 * @returns {Object} Mapped movie object
 */
const mapTmdbMovie = (tmdbMovie, genresMap) => ({
  id: tmdbMovie.id.toString(),
  title: tmdbMovie.title,
  year: tmdbMovie.release_date ? tmdbMovie.release_date.substring(0, 4) : 'N/A',
  poster: tmdbMovie.poster_path
    ? `${TMDB_IMAGE_BASE_URL}${tmdbMovie.poster_path}`
    : `${TMDB_IMAGE_BASE_URL}/w154/wwemzKWzjKYJFfCeiB57q3r4f3.png`,
  backdrop: tmdbMovie.backdrop_path
    ? `${TMDB_BACKDROP_BASE_URL}${tmdbMovie.backdrop_path}`
    : null,
  description: tmdbMovie.overview || 'No description available.',
  genres: (tmdbMovie.genre_ids || []).map(id => genresMap[id]).filter(Boolean),
  rating: tmdbMovie.vote_average ? tmdbMovie.vote_average.toFixed(1) : 'N/A',
});

/**
 * Generic API request wrapper with error handling
 * @param {string} url - API endpoint URL
 * @param {AbortSignal} signal - Optional abort signal for cancellation
 * @returns {Promise<{data: any, error: string|null}>}
 */
const apiRequest = async (url, signal = null) => {
  try {
    const options = signal ? { signal } : {};
    const response = await fetch(url, options);

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return { data, error: null };
  } catch (error) {
    // Handle abort errors gracefully
    if (error.name === 'AbortError') {
      return { data: null, error: 'Request cancelled' };
    }

    return {
      data: null,
      error: error.message || 'An unexpected error occurred'
    };
  }
};

/**
 * Fetch TMDb genres and create a map (ID -> Name)
 * @param {AbortSignal} signal - Optional abort signal
 * @returns {Promise<{data: Object|null, error: string|null}>}
 */
export const fetchGenres = async (signal = null) => {
  const url = `${TMDB_BASE_URL}/genre/movie/list?api_key=${TMDB_API_KEY}`;
  const { data, error } = await apiRequest(url, signal);

  if (error) {
    return { data: null, error: 'Failed to load movie genres.' };
  }

  // Convert array to map
  const genresMap = {};
  data.genres.forEach(genre => {
    genresMap[genre.id] = genre.name;
  });

  return { data: genresMap, error: null };
};

/**
 * Fetch movies from TMDb API
 * @param {Object} params - Search parameters
 * @param {string} params.query - Search query (empty for popular)
 * @param {string} params.genreId - Genre ID filter
 * @param {number} params.page - Page number
 * @param {Object} params.genresMap - Genre ID to name mapping
 * @param {AbortSignal} params.signal - Optional abort signal
 * @returns {Promise<{data: Object|null, error: string|null}>}
 */
export const fetchMovies = async ({ query = '', genreId = '', page = 1, genresMap = {}, signal = null }) => {
  let url = '';

  if (query.trim()) {
    url = `${TMDB_BASE_URL}/search/movie?api_key=${TMDB_API_KEY}&query=${encodeURIComponent(query)}&language=en-US&page=${page}`;
  } else if (genreId) {
    url = `${TMDB_BASE_URL}/discover/movie?api_key=${TMDB_API_KEY}&with_genres=${genreId}&language=en-US&sort_by=popularity.desc&page=${page}`;
  } else {
    url = `${TMDB_BASE_URL}/movie/popular?api_key=${TMDB_API_KEY}&language=en-US&page=${page}`;
  }

  const { data, error } = await apiRequest(url, signal);

  if (error) {
    return { data: null, error: 'Failed to load movies. Please try again later.' };
  }

  const movies = data.results.map(tmdbMovie => mapTmdbMovie(tmdbMovie, genresMap));

  return {
    data: {
      movies,
      page: data.page,
      totalPages: data.total_pages,
      totalResults: data.total_results,
    },
    error: null
  };
};

/**
 * Fetch movie trailer from TMDb
 * @param {string|number} movieId - Movie ID
 * @param {AbortSignal} signal - Optional abort signal
 * @returns {Promise<{data: string|null, error: string|null}>}
 */
export const fetchMovieTrailer = async (movieId, signal = null) => {
  const url = `${TMDB_BASE_URL}/movie/${movieId}/videos?api_key=${TMDB_API_KEY}&language=en-US`;
  const { data, error } = await apiRequest(url, signal);

  if (error) {
    return { data: null, error: 'Could not load trailer.' };
  }

  const trailer = data.results.find(
    (video) => video.site === 'YouTube' && video.type === 'Trailer'
  );

  if (!trailer) {
    return { data: null, error: 'No trailer available.' };
  }

  return {
    data: `https://www.youtube.com/embed/${trailer.key}`,
    error: null
  };
};

/**
 * Fetch similar movies from TMDb
 * @param {Object} params - Parameters
 * @param {string|number} params.movieId - Movie ID
 * @param {number} params.page - Page number
 * @param {Object} params.genresMap - Genre ID to name mapping
 * @param {AbortSignal} params.signal - Optional abort signal
 * @returns {Promise<{data: Object|null, error: string|null}>}
 */
export const fetchSimilarMovies = async ({ movieId, page = 1, genresMap = {}, signal = null }) => {
  const url = `${TMDB_BASE_URL}/movie/${movieId}/similar?api_key=${TMDB_API_KEY}&language=en-US&page=${page}`;
  const { data, error } = await apiRequest(url, signal);

  if (error) {
    return { data: null, error: 'Could not load similar movies.' };
  }

  const movies = data.results.map(tmdbMovie => mapTmdbMovie(tmdbMovie, genresMap));

  return {
    data: {
      movies,
      page: data.page,
      totalPages: data.total_pages,
    },
    error: null
  };
};

/**
 * Fetch main cast from TMDb for a movie
 * @param {string|number} movieId - Movie ID
 * @param {AbortSignal} signal - Optional abort signal
 * @returns {Promise<{data: Array|null, error: string|null}>}
 */
export const fetchMovieCast = async (movieId, signal = null) => {
  const url = `${TMDB_BASE_URL}/movie/${movieId}/credits?api_key=${TMDB_API_KEY}`;
  const { data, error } = await apiRequest(url, signal);

  if (error) {
    return { data: null, error: 'Could not load cast information.' };
  }

  const topCast = data.cast.slice(0, 6).map(actor => ({
    id: actor.id.toString(),
    name: actor.name,
    character: actor.character || 'N/A',
    profile_path: actor.profile_path
      ? `${TMDB_PROFILE_BASE_URL}${actor.profile_path}`
      : 'https://placehold.co/100x150/374151/D1D5DB?text=No+Photo',
  }));

  return { data: topCast, error: null };
};

/**
 * Fetch actor's movie credits
 * @param {Object} params - Parameters
 * @param {string|number} params.actorId - Actor ID
 * @param {number} params.page - Page number
 * @param {Object} params.genresMap - Genre ID to name mapping
 * @param {AbortSignal} params.signal - Optional abort signal
 * @returns {Promise<{data: Object|null, error: string|null}>}
 */
export const fetchActorMovies = async ({ actorId, page = 1, genresMap = {}, signal = null }) => {
  const url = `${TMDB_BASE_URL}/person/${actorId}/movie_credits?api_key=${TMDB_API_KEY}&language=en-US`;
  const { data, error } = await apiRequest(url, signal);

  if (error) {
    return { data: null, error: 'Could not load filmography.' };
  }

  const movies = data.cast.map(tmdbMovie => mapTmdbMovie(tmdbMovie, genresMap));
  const hasMore = data.cast.length > 0;

  return {
    data: {
      movies,
      page,
      hasMore,
    },
    error: null
  };
};

/**
 * Fetch actor details
 * @param {string|number} actorId - Actor ID
 * @param {AbortSignal} signal - Optional abort signal
 * @returns {Promise<{data: Object|null, error: string|null}>}
 */
export const fetchActorDetails = async (actorId, signal = null) => {
  if (!actorId) {
    return { data: null, error: 'Actor ID is required.' };
  }

  const url = `${TMDB_BASE_URL}/person/${actorId}?api_key=${TMDB_API_KEY}&language=en-US`;
  const { data, error } = await apiRequest(url, signal);

  if (error) {
    return { data: null, error: 'Could not load actor details.' };
  }

  return {
    data: {
      id: data.id.toString(),
      name: data.name,
      profile_path: data.profile_path
        ? `${TMDB_LARGE_PROFILE_BASE_URL}${data.profile_path}`
        : 'https://placehold.co/500x750/374151/D1D5DB?text=No+Photo',
      biography: data.biography || 'No biography available.',
    },
    error: null
  };
};

/**
 * Fetch watch providers for a movie from TMDb
 * @param {string|number} movieId - Movie ID
 * @param {AbortSignal} signal - Optional abort signal
 * @returns {Promise<{data: Object|null, error: string|null}>}
 */
export const fetchWatchProviders = async (movieId, signal = null) => {
  const url = `${TMDB_BASE_URL}/movie/${movieId}/watch/providers?api_key=${TMDB_API_KEY}`;
  const { data, error } = await apiRequest(url, signal);

  if (error) {
    return { data: null, error: 'Could not load watch providers.' };
  }

  // US is the most common, fallback to any country if not available
  const providers = data.results.US || Object.values(data.results)[0] || null;

  return { data: providers, error: null };
};

/**
 * Fetch all posters for a movie from TMDb
 * @param {string|number} movieId - Movie ID
 * @param {AbortSignal} signal - Optional abort signal
 * @returns {Promise<{data: Array|null, error: string|null}>}
 */
export const fetchMovieImages = async (movieId, signal = null) => {
  const url = `${TMDB_BASE_URL}/movie/${movieId}/images?api_key=${TMDB_API_KEY}`;
  const { data, error } = await apiRequest(url, signal);

  if (error) {
    return { data: [], error: null }; // Return empty array instead of error
  }

  const posters = (data.posters || []).map(
    poster => `${TMDB_IMAGE_BASE_URL}${poster.file_path}`
  );

  return { data: posters, error: null };
};

/**
 * Fetch a single movie's details by ID from TMDb
 * @param {string|number} movieId - Movie ID
 * @param {Object} genresMap - Genre ID to name mapping
 * @param {AbortSignal} signal - Optional abort signal
 * @returns {Promise<{data: Object|null, error: string|null}>}
 */
export const fetchMovieById = async (movieId, genresMap = {}, signal = null) => {
  const url = `${TMDB_BASE_URL}/movie/${movieId}?api_key=${TMDB_API_KEY}&language=en-US`;
  const { data, error } = await apiRequest(url, signal);

  if (error) {
    return { data: null, error: 'Could not load movie details.' };
  }

  const movie = mapTmdbMovie(data, genresMap);
  return { data: movie, error: null };
};

/**
 * Fetch a single actor's details by ID from TMDb
 * Alias for fetchActorDetails for backward compatibility
 * @param {string|number} actorId - Actor ID
 * @param {AbortSignal} signal - Optional abort signal
 * @returns {Promise<{data: Object|null, error: string|null}>}
 */
export const fetchActorById = async (actorId, signal = null) => {
  return fetchActorDetails(actorId, signal);
};
